/**
 * PlatformOwnedPluginsSection — the admin-only "Fias-owned plugins" block on the
 * contributor dashboard.
 *
 * WHY IT EXISTS. Annexation (docs/plugin-annexation.md) flips
 * `arches.contributor_id` to the Fias platform account, and the dashboard's own
 * list is filtered by `contributor_id = you` — so an annexed plugin vanishes from
 * the portal, taking with it the only path to the arche page that carries the
 * shared workspace asset-library controls (ADR-019). This section restores that
 * path for platform admins.
 *
 * DELIBERATELY SEPARATE from "Your Arches": these are not the viewer's arches and
 * their revenue is not in the Overview cards. Never merge the two lists.
 *
 * THE MAINTAINER BUTTON IS THE POINT. ADR-019 derives a link's validity from
 * "is some active member of this workspace an active `can_publish` collaborator
 * on this arche?". Taking a `publisher` row makes that true, which is what lets
 * an admin link a Fias-owned plugin to a workspace library — no new authority and
 * no exception in the validity predicate. Copy on the buttons says so plainly.
 *
 * Client gate mirrors `requireAdmin` on /v1/admin/plugins
 * (services/api-service/src/routes/plugins/platform-owned-admin.ts). It hides UI
 * only — the server is the authorization boundary.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { PlatformOwnedPluginArche, PlatformOwnedWorkspaceLink } from '@/lib/api';
import {
  usePlatformOwnedPlugins,
  usePluginMaintainers,
  useAddPluginMaintainer,
  useRemovePluginMaintainer,
} from '../hooks/usePlatformOwnedPlugins';
import { describeLinkInvalidReason } from './asset-library/link-validity';

// Matches the server's default page size. Fias owns a handful of plugins, so
// this is normally the whole list in one page; pagination is the overflow valve.
const PAGE_SIZE = 25;

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'unknown error';
}

/** Compact per-row view of the plugin's active workspace-library links. */
function WorkspaceLinkSummary({ links }: { links: PlatformOwnedWorkspaceLink[] }) {
  if (links.length === 0) {
    return <span className="text-xs text-muted-foreground">Not linked to any workspace</span>;
  }
  return (
    <ul className="space-y-1">
      {links.map((link) => (
        <li key={link.holderId} className="flex flex-wrap items-center gap-2">
          <span className="text-sm">{link.displayName}</span>
          {link.valid ? (
            <Badge variant="secondary">Valid</Badge>
          ) : (
            <>
              <Badge variant="destructive">Invalid</Badge>
              <span className="text-xs text-muted-foreground">
                {describeLinkInvalidReason(link.reason)}
              </span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function ProvenanceLine({ plugin }: { plugin: PlatformOwnedPluginArche }) {
  if (!plugin.isAnnexed) {
    return <Badge variant="outline">First-party</Badge>;
  }
  const transferred = plugin.contributorTransferredAt
    ? new Date(plugin.contributorTransferredAt).toLocaleDateString()
    : null;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Badge variant="outline">Annexed</Badge>
      <span className="text-xs text-muted-foreground">
        {/*
          Usernames are resolved server-side; a raw `usr_…` id must never reach
          the page. `resolveUserIds` degrades to an empty map on a KV failure or
          a deleted account, so this fallback IS reachable in normal operation —
          it renders a placeholder, the way the admin arche table renders an
          em-dash. The id is still on the payload for anyone debugging.
        */}
        from {plugin.priorContributorUsername ?? 'a former contributor'}
        {transferred ? ` · ${transferred}` : ''}
      </span>
    </span>
  );
}

interface MaintainerActionsProps {
  plugin: PlatformOwnedPluginArche;
  /**
   * The caller's own id. Used both to exclude themselves from the "who else can
   * publish?" count and as the identifier sent to the maintainer routes:
   * `resolveUserIdentifier` accepts a `usr_` id directly, so addressing the
   * account by id cannot go stale after a rename and skips a username lookup.
   */
  userId: string | null;
}

/**
 * Become / stop being a maintainer of one plugin.
 *
 * The revoke path opens a confirm dialog that loads the roster first, so it can
 * warn honestly when the caller is the only account that can publish: removing
 * themselves makes every workspace link on this plugin derive as
 * `no_qualifying_member`, and the next publish drops those shared assets.
 */
function MaintainerActions({ plugin, userId }: MaintainerActionsProps) {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const addMutation = useAddPluginMaintainer(plugin.archeId);
  const removeMutation = useRemovePluginMaintainer(plugin.archeId);
  // Only fetched once the dialog is open — the list view never pays for it.
  const maintainersQuery = usePluginMaintainers(plugin.archeId, { enabled: confirmOpen });

  // Per-link, server-derived. Counting other publishers is NOT a valid proxy:
  // link validity joins workspace membership on a SPECIFIC holder, so another
  // publisher preserves only the links to workspaces they themselves belong to.
  // A count can therefore promise "all links survive" while some or all break.
  const impact = maintainersQuery.data?.removalImpact ?? [];
  const breaking = impact.filter((l) => l.validNow && !l.wouldRemainValid);
  const surviving = impact.filter((l) => l.validNow && l.wouldRemainValid);

  if (!userId) {
    return (
      <span className="text-xs text-muted-foreground">
        Could not identify your account; maintainer actions are unavailable.
      </span>
    );
  }

  if (!plugin.youAreMaintainer) {
    return (
      <div className="space-y-1">
        <Button
          variant="outline"
          size="sm"
          disabled={addMutation.isPending}
          onClick={() => addMutation.mutate(userId)}
        >
          {addMutation.isPending ? 'Adding…' : 'Become maintainer'}
        </Button>
        <p className="text-xs text-muted-foreground">
          Grants you publish rights on this plugin — this is what lets you link it to a workspace
          asset library.
        </p>
        {addMutation.isError && (
          <p role="alert" className="text-xs text-destructive">
            Could not add you as a maintainer: {errorMessage(addMutation.error)}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
        Remove me
      </Button>
      {removeMutation.isError && (
        <p role="alert" className="text-xs text-destructive">
          Could not remove you: {errorMessage(removeMutation.error)}
        </p>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove yourself from {plugin.name}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  You lose publish rights on this plugin. You can add yourself back at any time.
                </p>
                {maintainersQuery.isLoading ? (
                  <p className="text-muted-foreground">
                    Checking which workspace links depend on you…
                  </p>
                ) : maintainersQuery.isError ? (
                  <p className="text-destructive">
                    Could not check the impact, so the effect on this plugin&apos;s workspace links
                    is unknown.
                  </p>
                ) : breaking.length > 0 ? (
                  <div className="space-y-2 rounded border border-destructive bg-destructive/5 p-3 text-destructive">
                    <p>
                      You are the last member of{' '}
                      {breaking.length === 1 ? 'this workspace' : 'these workspaces'} who can
                      publish this plugin. Removing yourself stops{' '}
                      {breaking.length === 1 ? 'its link' : 'their links'} qualifying, and the next
                      publish will drop those shared assets. The live published version keeps
                      serving what it already pinned until then.
                    </p>
                    <ul className="list-inside list-disc">
                      {breaking.map((l) => (
                        <li key={l.holderId}>{l.displayName}</li>
                      ))}
                    </ul>
                  </div>
                ) : surviving.length > 0 ? (
                  <p className="text-muted-foreground">
                    {surviving.length === 1 ? 'The workspace link' : 'All workspace links'} on this
                    plugin {surviving.length === 1 ? 'keeps qualifying' : 'keep qualifying'} —
                    another member of{' '}
                    {surviving.length === 1 ? 'that workspace' : 'each of those workspaces'} can
                    publish it.
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    No workspace link on this plugin is currently serving shared assets, so nothing
                    is dropped.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={removeMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                removeMutation.mutate(userId, { onSuccess: () => setConfirmOpen(false) });
              }}
            >
              {removeMutation.isPending ? 'Removing…' : 'Remove me'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function PlatformOwnedPluginsSection() {
  const { user } = useAuth();
  // Defence in depth: the dashboard also gates on this, but the component must
  // not fetch an admin endpoint if it is ever mounted elsewhere.
  const isAdmin = React.useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);

  const [offset, setOffset] = React.useState(0);

  const query = usePlatformOwnedPlugins({ limit: PAGE_SIZE, offset }, { enabled: isAdmin });

  if (!isAdmin) return null;

  const arches = query.data?.arches ?? [];
  const total = query.data?.total ?? 0;
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = offset + arches.length;

  return (
    <section className="mt-10 border-t pt-8" aria-labelledby="fias-owned-plugins-heading">
      <Card className="border-primary/40">
        <CardHeader>
          <CardTitle id="fias-owned-plugins-heading" className="flex flex-wrap items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            Fias-owned plugins
            <Badge variant="secondary">Admin only</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Plugins owned by the Fias platform account — annexed from a developer, or built
            first-party. They are not your arches, and their invocations and revenue are not
            included in the Overview above. To link one to a workspace asset library you must be a
            maintainer of it; open it with <span className="font-medium">Manage</span> and use the
            Shared workspace libraries section.
          </p>

          {query.isLoading ? (
            <div className="space-y-2" data-testid="platform-plugins-loading">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : query.isError ? (
            <div role="alert" className="text-sm text-destructive">
              Could not load Fias-owned plugins: {errorMessage(query.error)}
            </div>
          ) : arches.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No plugins are owned by the Fias platform account yet.
            </div>
          ) : (
            <ul className="divide-y rounded border">
              {arches.map((plugin) => (
                <li key={plugin.archeId} className="flex flex-col gap-3 p-4 lg:flex-row">
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{plugin.name}</span>
                      <Badge variant="secondary">{plugin.status}</Badge>
                      {plugin.lifecycleState !== 'active' && (
                        <Badge variant="destructive">{plugin.lifecycleState}</Badge>
                      )}
                      <ProvenanceLine plugin={plugin} />
                    </div>
                    <div className="font-mono text-xs text-muted-foreground">{plugin.archeId}</div>
                    <div>
                      <div className="text-xs font-medium text-muted-foreground">
                        Workspace libraries
                      </div>
                      <WorkspaceLinkSummary links={plugin.workspaceLinks} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      You:{' '}
                      {plugin.youAreMaintainer ? (
                        <span className="font-medium text-foreground">maintainer</span>
                      ) : (
                        'not a maintainer'
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col gap-2 lg:w-64 lg:items-end">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/contributor/arches/${plugin.archeId}`}>Manage</Link>
                    </Button>
                    <MaintainerActions plugin={plugin} userId={user?.userId ?? null} />
                  </div>
                </li>
              ))}
            </ul>
          )}

          {total > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {rangeStart}–{rangeEnd} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0 || query.isFetching}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={rangeEnd >= total || query.isFetching}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
